/**
 * Client Entry Point
 * 
 * Hydrates the server-rendered React app on the client side.
 * Includes Bloomcode providers (Apollo, Auth, Theme, i18n)
 */

import { StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Providers } from './providers';
import { App } from './App';
import './index.css';

// Register service worker for caching
if ('serviceWorker' in navigator && typeof window !== 'undefined') {
  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        updateViaCache: 'none', // Never use HTTP cache for sw.js
      });
      
      // Check for updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New service worker available - show update prompt or auto-reload
              console.log('New version available! Refreshing...');
              newWorker.postMessage('skipWaiting');
            }
          });
        }
      });
      
      // Listen for controller change and reload
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshing) {
          refreshing = true;
          window.location.reload();
        }
      });
    } catch (err) {
      console.log('Service worker registration failed:', err);
    }
  });
}

// Use hydrateRoot to hydrate the server-rendered HTML
// onRecoverableError logs detailed hydration mismatch info
hydrateRoot(
  document.getElementById('root')!,
  <StrictMode>
    <HelmetProvider>
      <BrowserRouter>
        <Providers>
          <App />
        </Providers>
      </BrowserRouter>
    </HelmetProvider>
  </StrictMode>,
  {
    onRecoverableError: (error, errorInfo) => {
      console.error('Hydration error:', error);
      console.error('Error info:', errorInfo);
      // Log the component stack to identify the exact component causing the issue
      if (errorInfo.componentStack) {
        console.error('Component stack:', errorInfo.componentStack);
      }
    },
  }
);
